import { useState, useEffect } from "react";
import { Users, Gift, Copy, Trophy, DollarSign, Link2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { usd, timeAgo, clsx } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setData(r.data));
    api.get("/referrals/leaderboard").then((r) => setBoard(r.data));
  }, []);

  if (!data) return <div className="p-8 text-fx-s3">Loading…</div>;
  const link = `${window.location.origin}/register?ref=${data.code}`;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied to clipboard"); };

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-[1200px]">
      <div className="flex items-center gap-2"><Gift className="h-6 w-6 text-brandblue" /><h1 className="font-display text-3xl font-extrabold tracking-tight">Referrals</h1></div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-xl bg-fx-s1 border border-white/5 p-5" data-testid="ref-total-referrals"><Users className="h-4 w-4 text-fx-s3" /><div className="font-mono text-3xl font-bold mt-2">{data.total_referrals}</div><div className="text-xs text-fx-s3">Total referrals</div></div>
        <div className="rounded-xl bg-fx-s1 border border-white/5 p-5" data-testid="ref-total-earnings"><DollarSign className="h-4 w-4 text-fx-s3" /><div className="font-mono text-3xl font-bold mt-2 text-pos">{usd(data.total_earnings)}</div><div className="text-xs text-fx-s3">Commission earned</div></div>
        <div className="rounded-xl bg-fx-s1 border border-white/5 p-5"><Gift className="h-4 w-4 text-fx-s3" /><div className="font-mono text-3xl font-bold mt-2">{data.code}</div><div className="text-xs text-fx-s3">Your referral code</div></div>
      </div>

      <div className="rounded-xl bg-gradient-to-br from-brandblue/15 to-fx-s1 border border-brandblue/20 p-6">
        <div className="flex items-center gap-2 text-brandblue"><Link2 className="h-5 w-5" /><span className="font-display font-bold">Your referral link</span></div>
        <p className="text-sm text-fx-s3 mt-1">Earn commission every time someone you invite funds their account.</p>
        <div className="flex gap-2 mt-4">
          <Input readOnly value={link} data-testid="ref-link" className="bg-fx-base border-white/10 font-mono text-sm" />
          <Button onClick={() => copy(link)} data-testid="ref-copy-btn" className="bg-brandblue hover:bg-blue-500 gap-2 shrink-0"><Copy className="h-4 w-4" /> Copy</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl bg-fx-s1 border border-white/5">
          <div className="px-5 py-4 border-b border-white/5"><h2 className="font-display font-bold">Referred Users</h2></div>
          <div className="divide-y divide-white/[0.03] max-h-72 overflow-y-auto">
            {data.referred_users.length === 0 && <div className="px-5 py-8 text-center text-fx-s3 text-sm">No referrals yet. Share your link!</div>}
            {data.referred_users.map((u, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3">
                <div><div className="text-sm font-medium">{u.name}</div><div className="text-xs text-fx-s3">{u.email}</div></div>
                <span className="text-xs text-fx-s3">{timeAgo(u.created_at)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-fx-s1 border border-white/5">
          <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2"><Trophy className="h-4 w-4 text-gold" /><h2 className="font-display font-bold">Leaderboard</h2></div>
          <div className="divide-y divide-white/[0.03] max-h-72 overflow-y-auto">
            {board.length === 0 && <div className="px-5 py-8 text-center text-fx-s3 text-sm">No commissions earned yet.</div>}
            {board.map((b, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <span className={clsx("h-7 w-7 rounded-full grid place-items-center font-mono text-xs font-bold", i === 0 ? "bg-gold text-black" : "bg-fx-s2 text-fx-s3")}>{i + 1}</span>
                  <span className="text-sm font-medium">{b.name}</span>
                </div>
                <span className="font-mono text-pos font-semibold">{usd(b.earnings)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-fx-s1 border border-white/5">
        <div className="px-5 py-4 border-b border-white/5"><h2 className="font-display font-bold">Commission History</h2></div>
        <div className="divide-y divide-white/[0.03] max-h-72 overflow-y-auto">
          {data.commissions.length === 0 && <div className="px-5 py-8 text-center text-fx-s3 text-sm">No commissions yet.</div>}
          {data.commissions.map((c) => (
            <div key={c.id} data-testid={`commission-${c.id}`} className="flex items-center justify-between px-5 py-3">
              <div><div className="text-sm font-medium">From {c.referred_name || "referral"}</div><div className="text-xs text-fx-s3">{c.pct}% · {timeAgo(c.created_at)}</div></div>
              <span className="font-mono text-pos font-semibold">+{usd(c.amount)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
